/**
 * Camera stream URLs — put the hub back in front of the relative WHEP path.
 *
 * The hub's `GET /cameras/:id/go2rtc/endpoints` deliberately answers a
 * SAME-ORIGIN path (`/api/cameras/<id>/go2rtc/whep`, alongside
 * `absoluteWhepUrl`) since `c304b5b`: an absolute `http://` URL breaks a
 * browser watching a hub served over TLS (mixed content). A page the hub
 * serves itself is same-origin, so the relative path is exactly right there.
 *
 * Every OTHER client loads the SPA from an origin that is not the hub — the
 * desktop app from `file://`, the phone from `capacitor://localhost`/its own
 * scheme — and there a leading-`/` path resolves to the APP's origin. The
 * request then never leaves the app: `services/hubRequestRouting` classifies a
 * relative `/api/…` as cloud-bound (`resolvesToCloudOrigin` matches the path),
 * refuses the plain-HTTP fallback and `routedFetch` throws
 * `Hub API request not sent: the WebRTC channel to the hub is not connected`,
 * while a raw `fetch('/api/…')` on a `file://` page throws
 * `TypeError: Failed to fetch`. The visible symptom is cameras that never play
 * (and never reach the hub: no `[WHEP Proxy]` line in its log) while snapshots
 * — absolute URLs — are fine.
 *
 * So: resolve the path against the HUB's own base when that base is a direct
 * hub (`otto-local://<host>` in Electron, a LAN address on the phone). A cloud
 * base is left alone — there the WebRTC channel carries hub paths and the path
 * alone is what it wants — and so is a hub-served page's own relative base.
 */
import { isDirectHubUrl } from './hubRequestRouting';

/**
 * Resolve a relative hub stream path (`/api/…`) against `apiUrl`.
 *
 * Returns the input untouched when it is already absolute, when `apiUrl` is a
 * same-origin/relative base (the page IS the hub, or the cloud app), or when
 * the base is a cloud origin (the channel carries the path, an absolute cloud
 * URL would be wrong). `apiUrl` is `RuntimeConfig.apiUrl`.
 */
export function hubAbsoluteStreamUrl(url: string, apiUrl: string): string {
  if (!url.startsWith('/')) {
    return url;
  }

  const base = (apiUrl || '').trim();
  if (!base || base.startsWith('/')) {
    return url;
  }

  // Cloud (or any web) base: a hub path stays a path for the WebRTC channel.
  if (!isDirectHubUrl(base)) {
    return url;
  }

  try {
    // An absolute path replaces the base's own path, so a base spelled
    // `http://<hub>/api` joins correctly.
    return new URL(url, base).toString();
  } catch {
    return url;
  }
}
